import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface WorkflowLayoutProps {
  header: ReactNode;
  sidebar?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * WorkflowLayout provides a proper non-overlapping layout for the workflow page.
 *
 * Structure:
 *  ┌──────────────────────────────────┐
 *  │            HEADER                │  ← sticky top, full width
 *  ├─────────┬────────────────────────┤
 *  │         │                        │
 *  │ SIDEBAR │     MAIN CONTENT       │  ← fills remaining height
 *  │         │                        │
 *  │  (scroll│      (scrollable)      │
 *  │   able) │                        │
 *  │         ├────────────────────────┤
 *  │         │       FOOTER           │  ← sticky bottom of main area
 *  └─────────┴────────────────────────┘
 *
 * Key: No fixed positioning. No z-index. No magic pixel offsets.
 * Everything flows naturally via flexbox.
 */
export const WorkflowLayout = ({
  header,
  sidebar,
  footer,
  children,
  className,
}: WorkflowLayoutProps) => {
  return (
    <div className={cn('flex h-screen flex-col overflow-hidden bg-background', className)}>
      {/* Header — sticky at top, never overlapped */}
      <div className="flex-none border-b border-border bg-card shadow-sm">
        {header}
      </div>

      {/* Body — sidebar + main, fills remaining height */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — fixed width, self-scrolling (only rendered when provided) */}
        {sidebar && (
          <div className="flex-none w-72 border-r border-border bg-card overflow-y-auto">
            {sidebar}
          </div>
        )}

        {/* Main area — flex column: scrollable content + sticky footer */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <div className="max-w-5xl mx-auto">
                {children}
              </div>
            </div>
          </div>

          {/* Footer — pinned to bottom of main area, never overlaps sidebar */}
          {footer && (
            <div className="flex-none border-t border-border bg-card shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
